import { defineLeerplusTypes, getDefaultState, ISaveState } from '@gametailors/leerplus';
import { StudioApiDataPackageProvider, StudioDataInstance, StudioDataPackage } from '@gametailors/lp-studio-connector';
import { generateUid } from '@gametailors/web-core/misc';
import { LpRidMechanicConfig, LpSchema } from '../../typings/schema';

// all positions are in pixels of the book's content box (both pages together)
export interface IRidPlakboekjeRewardContent {
  type: 'REWARD';
  uid: string; // the RidScrapbookReward
  stopUid?: string; // the RidLocationStop that granted it, so each stop grants its reward once; unset when granted outside a stop
  x: number;
  y: number;
  rotation: number;
}

export interface IRidPlakboekjeDrawingContent {
  type: 'DRAWING';
  color: string | null; // null erases
  width: number;
  points: number[]; // the recorded pointer positions, flattened [x0, y0, x1, y1, ...]; smoothed into a curve when rendered
}

/** Anything stuck or drawn into the scrapbook. Everything shares one z-index counter, so items stack in the order they were added. */
export interface IRidPlakboekjeItem {
  uid: string;
  week: number;
  zIndex: number;
  content: IRidPlakboekjeRewardContent | IRidPlakboekjeDrawingContent;
}

export interface IRidCodeLeerplusSaveState extends ISaveState {
  mechanics: {
    leeslijst_stopwatch: {
      bestTime: number | null;
      bestAccuracy: number | null;
    };
  };
  plakboekje: IRidPlakboekjeItem[];
  /** The date the game plays on (YYYY-MM-DD), set from the debug panel to test other days. Null plays on today. */
  date: string | null;
  homework: {
    weekIndex: number;
    stops: {
      id: string;
      assignments: {
        /** The backend's assignment uid. Absent for placeholder homework that isn't stored anywhere. */
        uid?: string;
        config: LpRidMechanicConfig;
      }[];
    }[];
  };
  homeworkLinks: {
    stops: {
      homeworkStopId: string;
      locationStopUid: string;
      linkedDate: string;
      assignmentActivityUids: Record<string, string>; // assignment config.uid -> activity uid
    }[];
  };
  /** The province and location the player chose to work through. A choice is over once it's finished, then the player chooses again. */
  journey: {
    provinceUid: string | null;
    locationUid: string | null;
  };
}

export function getDefaultCodeSave(): IRidCodeLeerplusSaveState {
  return {
    ...getDefaultState(),
    mechanics: {
      leeslijst_stopwatch: {
        bestTime: null,
        bestAccuracy: null,
      },
    },
    homeworkLinks: { stops: [] },
    journey: { provinceUid: null, locationUid: null },
    plakboekje: [],
    date: null,
    // TODO: this is a placeholder. in practice the default save should be empty ofc.
    homework: {
      weekIndex: 0,
      stops: [
        {
          id: generateUid(),
          assignments: [
            {
              config: {
                dataType: 'RidWisselklankenConfig',
                count: 3,
                unit: 'KLANK',
                difficulty: 'EASY',
                uid: generateUid(),
                topicFilters: {
                  dataType: 'TopicFilters',
                  filters: [
                    {
                      dataType: 'TopicFilter',
                      groups: [
                        {
                          dataType: 'StringList',
                          strings: [
                            {
                              dataType: 'String',
                              string: 'dieren',
                              uid: generateUid(),
                            },
                          ],
                          uid: generateUid(),
                        },
                      ],
                      uid: generateUid(),
                    },
                  ],
                  index: 0,
                  uid: generateUid(),
                },
              },
            },
            {
              config: {
                dataType: 'RidLeeslijstConfig',
                uid: generateUid(),
                mode: {
                  dataType: 'RidLeeslijstConfigModeTimer',
                  duration: 8,
                  pages: 3,
                  uid: generateUid(),
                  topicFilters: {
                    dataType: 'TopicFilters',
                    filters: [
                      {
                        dataType: 'TopicFilter',
                        groups: [
                          {
                            dataType: 'StringList',
                            strings: [
                              {
                                dataType: 'String',
                                string: 'dieren',
                                uid: generateUid(),
                              },
                            ],
                            uid: generateUid(),
                          },
                        ],
                        uid: generateUid(),
                      },
                    ],
                    index: 0,
                    uid: generateUid(),
                  },
                },
              },
            },
          ],
        },
        {
          id: generateUid(),
          assignments: [
            {
              config: {
                dataType: 'RidWisselklankenConfig',
                count: 3,
                unit: 'KLANK',
                difficulty: 'EASY',
                uid: generateUid(),
                topicFilters: {
                  dataType: 'TopicFilters',
                  filters: [
                    {
                      dataType: 'TopicFilter',
                      groups: [
                        {
                          dataType: 'StringList',
                          strings: [
                            {
                              dataType: 'String',
                              string: 'eten',
                              uid: generateUid(),
                            },
                          ],
                          uid: generateUid(),
                        },
                      ],
                      uid: generateUid(),
                    },
                  ],
                  index: 0,
                  uid: generateUid(),
                },
              },
            },
          ],
        },
      ],
    },
  };
}

export const LEERPLUS = defineLeerplusTypes<IRidCodeLeerplusSaveState, LpSchema>();

export class ExpandableDataPackageProvider extends StudioApiDataPackageProvider {
  private instances: StudioDataInstance[] = [];

  public include(instances: StudioDataInstance[]): void {
    this.instances.push(...instances);
  }

  override async getDataPackage(): Promise<StudioDataPackage> {
    const basePackage = await super.getDataPackage();
    return {
      ...basePackage,
      instances: [...basePackage.instances, ...this.instances],
    };
  }
}
